import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import ClientSlider from '@/components/ClientSlider';
import SEO, { generateOrganizationSchema, generateWebsiteSchema } from '@/components/SEO';
import { getTeam, getBlogs, getStats } from '@/lib/api';
import { SERVICES } from '@/lib/services';
import { trackCTA, trackBookCall } from '@/lib/gtm';
import { ArrowRight, ArrowUpRight, CheckCircle, Linkedin, ChevronLeft, ChevronRight, Sparkles, Zap, Star } from 'lucide-react';

const defaultStats = [
  { value: "150+", label: "Brands Served" },
  { value: "5.4x", label: "Avg ROAS Lift" },
  { value: "100%", label: "In-House Craft" },
  { value: "10M+", label: "Revenue Generated" },
];

const values = [
  "Strategy before craft",
  "Numbers before opinions",
  "Speed without sloppiness",
  "Partners, not vendors",
  "Brand + performance, fused",
  "One team, one P&L",
];

const marquee = [
  "Creative", "Media", "Technology", "Performance", "Brand", "Commerce", "Content", "Code"
];

export default function Home() {
  const [team, setTeam] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [stats, setStats] = useState(defaultStats);
  const sliderRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [teamRes, blogRes, statsRes] = await Promise.all([getTeam(), getBlogs(), getStats()]);
      setTeam(teamRes.data.slice(0, 3));
      setBlogs(blogRes.data.slice(0, 3));
      if (statsRes.data.length > 0) setStats(statsRes.data);
    } catch (err) { console.error('Error loading data:', err); }
  };

  const scrollSlider = (dir) => {
    if (!sliderRef.current) return;
    const card = sliderRef.current.querySelector('[data-service-card]');
    const cardWidth = card ? card.offsetWidth + 24 : 360;
    sliderRef.current.scrollBy({ left: dir * cardWidth, behavior: 'smooth' });
  };

  const onScroll = () => {
    if (!sliderRef.current) return;
    const card = sliderRef.current.querySelector('[data-service-card]');
    const cardWidth = card ? card.offsetWidth + 24 : 360;
    setActiveIdx(Math.round(sliderRef.current.scrollLeft / cardWidth));
  };

  return (
    <div className="min-h-screen" style={{ background: '#FAFAF7' }} data-testid="home-page">
      <SEO
        title="Creative · Media · Technology Agency"
        description="Wizbang is India's creative, media and technology agency. We build revenue systems through performance marketing, e-commerce, web development, custom CRMs, packaging and branding."
        keywords="creative agency India, media agency, technology agency, performance marketing agency, ecommerce development, web development, custom CRM, packaging design, branding agency, digital marketing"
        url="/"
        structuredData={{ "@context": "https://schema.org", "@graph": [generateOrganizationSchema(), generateWebsiteSchema()] }}
      />
      <Navbar />

      {/* ============ HERO ============ */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-radial opacity-80" />
        <div className="absolute top-40 right-10 w-72 h-72 rounded-full blur-3xl opacity-30" style={{ background: 'radial-gradient(circle, #ff5a1f, transparent)' }} />

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="grid lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-9">
              <div className="eyebrow mb-6 animate-fadeInUp">Creative · Media · Technology</div>
              <h1 className="font-syne text-[44px] sm:text-6xl lg:text-[88px] font-bold leading-[0.95] text-[#0A0A0A] mb-8 animate-fadeInUp stagger-1 tracking-tight">
                We turn brands into <span className="font-editorial text-[#ff5a1f]">revenue</span><br />
                machines that <span className="text-gradient-orange">never sleep.</span>
              </h1>
              <p className="text-[#3A3A3A] text-lg lg:text-xl max-w-2xl mb-10 leading-relaxed animate-fadeInUp stagger-2">
                Six disciplines. One studio. Performance marketing, e-commerce, web, custom CRMs, packaging and branding — engineered to compound.
              </p>

              <div className="flex flex-wrap gap-4 animate-fadeInUp stagger-3">
                <Link to="/book-appointment" data-testid="hero-cta-primary" onClick={() => trackBookCall('home_hero')}
                  className="bg-[#0A0A0A] text-white px-7 py-4 text-sm font-semibold rounded-full hover:bg-[#ff5a1f] transition-colors duration-300 btn-press inline-flex items-center gap-2">
                  Book a growth call
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
                <Link to="/services" data-testid="hero-cta-secondary" onClick={() => trackCTA('hero_see_services', 'home_hero')}
                  className="border-2 border-[#0A0A0A] text-[#0A0A0A] px-7 py-4 text-sm font-semibold rounded-full hover:bg-[#0A0A0A] hover:text-white transition-colors duration-300 inline-flex items-center gap-2">
                  See what we do
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            <div className="lg:col-span-3 hidden lg:block">
              <div className="bg-white border rounded-2xl p-6 shadow-sm" style={{ borderColor: 'var(--border)' }}>
                <div className="flex items-center gap-2 mb-3">
                  {[1,2,3,4,5].map(i => <Star key={i} className="w-4 h-4 fill-[#ff5a1f] text-[#ff5a1f]" />)}
                </div>
                <p className="text-sm text-[#0A0A0A] leading-relaxed mb-4">
                  "Wizbang doesn't think like an agency. They think like a co-founder."
                </p>
                <div className="text-xs text-[#5B5B5B]">— Founder, D2C beauty brand</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ MARQUEE ============ */}
      <section className="dark-section py-6 overflow-hidden">
        <div className="marquee-track animate-ticker">
          {[...marquee, ...marquee, ...marquee].map((w, i) => (
            <div key={i} className="flex items-center gap-12 text-white font-syne text-3xl md:text-4xl font-bold tracking-tight whitespace-nowrap">
              <span>{w}</span>
              <Sparkles className="w-5 h-5 text-[#ff5a1f]" />
            </div>
          ))}
        </div>
      </section>

      {/* ============ SERVICES SLIDER ============ */}
      <section className="py-24 bg-[#FAFAF7]" data-testid="services-slider-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-12">
            <div className="max-w-2xl">
              <div className="eyebrow mb-5">What we do</div>
              <h2 className="font-syne text-4xl md:text-5xl font-bold text-[#0A0A0A] mb-4 tracking-tight">
                Six disciplines. <span className="font-editorial text-[#ff5a1f]">One unfair advantage.</span>
              </h2>
              <p className="text-[#5B5B5B] text-base md:text-lg leading-relaxed">
                Specialists in performance, commerce, code, branding and packaging — collaborating in the same room, on the same brief, against the same outcome.
              </p>
            </div>
            <div className="flex items-center gap-3" data-testid="services-slider-controls">
              <button onClick={() => scrollSlider(-1)} aria-label="Previous service" data-testid="services-prev-btn"
                className="w-12 h-12 rounded-full border-2 border-[#0A0A0A] flex items-center justify-center hover:bg-[#0A0A0A] hover:text-white transition-colors">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button onClick={() => scrollSlider(1)} aria-label="Next service" data-testid="services-next-btn"
                className="w-12 h-12 rounded-full bg-[#0A0A0A] text-white flex items-center justify-center hover:bg-[#ff5a1f] transition-colors">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div ref={sliderRef} onScroll={onScroll}
            className="service-slider flex gap-6 overflow-x-auto pb-4 -mx-6 md:-mx-12 px-6 md:px-12"
            data-testid="services-slider">
            {SERVICES.map((s, idx) => (
              <Link key={s.slug} to={`/services/${s.slug}`} data-service-card
                data-testid={`service-slide-${s.slug}`}
                className="group flex-shrink-0 w-[88vw] sm:w-[420px] bg-white border rounded-2xl p-8 hover:border-[#ff5a1f] transition-all duration-500 card-tilt"
                style={{ borderColor: 'var(--border)' }}>
                <div className="flex items-start justify-between mb-6">
                  <div className="w-14 h-14 rounded-xl bg-[#ff5a1f]/10 flex items-center justify-center group-hover:bg-[#ff5a1f] transition-colors">
                    <s.icon className="w-7 h-7 text-[#ff5a1f] group-hover:text-white transition-colors" />
                  </div>
                  <span className="font-mono text-xs text-[#8A8A8A]">0{idx + 1}/0{SERVICES.length}</span>
                </div>
                <h3 className="font-syne text-2xl font-bold text-[#0A0A0A] mb-2 tracking-tight group-hover:text-[#ff5a1f] transition-colors">
                  {s.title}
                </h3>
                <p className="font-editorial text-[#ff5a1f] mb-4 text-base">{s.tagline}</p>
                <p className="text-[#5B5B5B] text-sm leading-relaxed mb-8">{s.short}</p>
                <div className="inline-flex items-center gap-2 text-[#0A0A0A] font-semibold text-sm">
                  Explore service
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover:rotate-45" />
                </div>
              </Link>
            ))}
          </div>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-8" data-testid="services-slider-dots">
            {SERVICES.map((_, idx) => (
              <button key={idx} aria-label={`Go to service ${idx + 1}`} data-testid={`slider-dot-${idx}`}
                onClick={() => {
                  const card = sliderRef.current?.querySelector('[data-service-card]');
                  const cw = card ? card.offsetWidth + 24 : 360;
                  sliderRef.current?.scrollTo({ left: cw * idx, behavior: 'smooth' });
                }}
                className={`h-2 rounded-full transition-all ${activeIdx === idx ? 'w-8 bg-[#ff5a1f]' : 'w-2 bg-[#D9D5C9]'}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ============ CLIENTS ============ */}
      <section className="py-16 bg-[#FAFAF7]" data-testid="clients-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-center text-[#8A8A8A] text-xs uppercase tracking-widest mb-10 font-mono">
            // Brands compounding with Wizbang
          </p>
          <ClientSlider />
        </div>
      </section>

      {/* ============ APPROACH ============ */}
      <section className="py-24 bg-white" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="eyebrow mb-5">Our approach</div>
              <h2 className="font-syne text-4xl md:text-5xl font-bold text-[#0A0A0A] mb-6 tracking-tight">
                Brand <span className="font-editorial text-[#ff5a1f]">and</span> performance.<br />
                In the same room.
              </h2>
              <p className="text-[#5B5B5B] mb-10 leading-relaxed text-base md:text-lg">
                We refuse the old agency split. Strategists, designers, performance marketers and engineers sit on one team. The brief is shared. The number is shared. The output is the kind of work that wins both pitch decks and quarterly reviews.
              </p>

              <div className="grid grid-cols-2 gap-4">
                {values.map((value, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="mt-1 w-5 h-5 rounded-full bg-[#ff5a1f]/15 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-3 h-3 text-[#ff5a1f]" />
                    </div>
                    <span className="text-[#0A0A0A] text-sm font-medium">{value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="dark-section aspect-square rounded-3xl p-10 flex flex-col justify-between relative overflow-hidden">
                <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full blur-3xl opacity-50" style={{ background: 'radial-gradient(circle, #ff5a1f, transparent)' }} />
                <Zap className="w-8 h-8 text-[#ff5a1f] relative" />
                <div className="relative">
                  <p className="font-syne text-7xl md:text-8xl font-bold text-white tracking-tighter">5x</p>
                  <p className="text-white font-syne text-2xl mt-4">Average growth rate</p>
                  <p className="text-white/60 text-sm mt-2">in the first 6 months of partnership</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

  
      {/* ============ CTA ============ */}
      <section className="py-24 bg-[#FAFAF7] relative overflow-hidden" data-testid="cta-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center relative z-10">
          <div className="eyebrow mb-6 justify-center" style={{ display: 'inline-flex' }}>Let's build something</div>
          <h2 className="font-syne text-4xl md:text-6xl lg:text-7xl font-bold text-[#0A0A0A] mb-8 tracking-tight">
            Ready to build a <span className="font-editorial text-[#ff5a1f]">revenue system</span> your competitors will study?
          </h2>
          <p className="text-[#5B5B5B] text-lg mb-12 max-w-2xl mx-auto">
            One call. Forty minutes. We'll sketch the architecture of your next growth phase live, with you.
          </p>
          <Link to="/book-appointment" data-testid="cta-book-btn" onClick={() => trackBookCall('home_bottom_cta')}
            className="bg-[#0A0A0A] text-white px-10 py-5 text-sm font-semibold rounded-full hover:bg-[#ff5a1f] transition-colors duration-300 btn-press inline-flex items-center gap-2">
            Book your growth call
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
